import { Button, TextField } from '@react-spectrum/s2';
import { useRef, useState } from 'react';

import { IMAGE_FILE_INPUT_ACCEPT } from '@/api/fileUploadApi.ts';
import { BlobImage } from '@/components/blob/BlobImage';

import { resolveTarget, readPropertyValue } from '../targetResolvers.ts';
import type { EditPatch, EditableDescriptor } from '../types.ts';

type ImagePopupProps = {
	descriptor: EditableDescriptor;
	iframeDoc: Document;
	brandImages?: { display_url: string }[] | null;
	onApplyEdit: (
		descriptor: EditableDescriptor,
		patch: Partial<EditPatch>,
	) => void;
	onImageUpload?: (file: File) => Promise<string | null>;
	isUploading?: boolean;
};

export const ImagePopup = ({
	descriptor,
	iframeDoc,
	brandImages,
	onApplyEdit,
	onImageUpload,
	isUploading = false,
}: ImagePopupProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const element = iframeDoc.getElementById(descriptor.id);

	const hasAnchor = element
		? resolveTarget(element, 'img', 'href') !== null
		: false;
	const currentHref = element
		? readPropertyValue(element, 'img', 'href') || ''
		: '';

	const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file || !onImageUpload) return;
		setUploadError(null);
		try {
			const url = await onImageUpload(file);
			if (!url) {
				setUploadError('Upload failed. Please try again.');
				return;
			}
			onApplyEdit(descriptor, { src: url });
		} catch {
			setUploadError('Upload failed. Please try again.');
		} finally {
			// Reset input so re-uploading same file triggers change
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	};

	const handleBrandImageClick = (url: string) => {
		setUploadError(null);
		onApplyEdit(descriptor, { src: url });
	};

	return (
		<div className="flex min-h-0 flex-col gap-3">
			<div
				className="text-xs font-medium tracking-wider text-gray-500 uppercase"
			>
				Image
			</div>

			<div>
				<input
					ref={fileInputRef}
					type="file"
					accept={IMAGE_FILE_INPUT_ACCEPT}
					className="hidden"
					onChange={handleFileSelect}
				/>
				<Button
					variant="secondary"
					onPress={() => fileInputRef.current?.click()}
					isPending={isUploading}
					isDisabled={isUploading}
				>
					Upload image
				</Button>
				{uploadError && (
					<div className="mt-1 text-xs text-red-600">{uploadError}</div>
				)}
			</div>

			{brandImages && brandImages.length > 0 && (
				<div>
					<div className="mb-1 text-xs text-gray-400">Your brand images</div>
					<div className="grid grid-cols-3 gap-1">
						{brandImages.map((img) => (
							<button
								key={img.display_url}
								type="button"
								className="overflow-hidden rounded border border-gray-200
									transition-colors hover:border-blue-400"
								onClick={() => handleBrandImageClick(img.display_url)}
							>
								<BlobImage
									src={img.display_url}
									alt=""
									className="h-16 w-full object-cover"
								/>
							</button>
						))}
					</div>
				</div>
			)}

			{hasAnchor && (
				<TextField
					label="Link URL"
					defaultValue={currentHref}
					onChange={(value) => onApplyEdit(descriptor, { href: value })}
				/>
			)}
		</div>
	);
};
